import React, { useState, useEffect } from 'react';
import Cart from './Cart';
import TermsAndConditionsPopup from './TermsAndConditionsPopup';
import OrderCompletionPopup from './OrderCompletionPopup';
import { BACKEND_URL } from '../config';
import { currentPaymentSplit } from '../utils/currentPaymentSplit';
import './CheckoutPage.css';

// Define currency symbols
const currencySymbols = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
  MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
};

const CheckoutPage = ({
  cartItems: initialCartItems,
  removeFromCart,
  completeCheckout,
  cancelCheckout,
  goToMainPage,
  userSettings,
  username,
  calculatePrice,
  togglePaymentType,
  resetApp,
  clearCart,
  paymentSplitData: savedPaymentSplitData,
  onSaveForLater,
  onLoadBuylist,
  setCartItems: setParentCartItems
}) => {
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localCartItems, setLocalCartItems] = useState(initialCartItems);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const paymentSplitData = currentPaymentSplit(localCartItems, savedPaymentSplitData, userSettings);
  const shouldUsePaymentSplit = Boolean(paymentSplitData);

  useEffect(() => {
    // Log the initial cart items to verify the basePrice
    console.log('Initial cart items:', initialCartItems);
    setLocalCartItems(initialCartItems);
    
    // Log the backend URL being used
    console.log('Backend URL:', BACKEND_URL);
  }, [initialCartItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAgreed) {
      setError('You must agree to the terms and conditions.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    console.log('Full cart items data:', localCartItems);

        // Create a local copy of orderNotes to collect additional notes from items
        let collectedOrderNotes = orderNotes;

        // Add payment split info to order notes if enabled
        if (shouldUsePaymentSplit) {
          const currencySymbol = currencySymbols[userSettings?.currency || 'USD'] || '$';
          const splitNote = `\n\n--- Payment Split ---\nCash: ${currencySymbol}${paymentSplitData.cashAmount.toFixed(2)} (${paymentSplitData.cashPercentage.toFixed(1)}%)\nCredit: ${currencySymbol}${paymentSplitData.creditAmount.toFixed(2)} (${paymentSplitData.creditPercentage.toFixed(1)}%)`;
          collectedOrderNotes = collectedOrderNotes ? collectedOrderNotes + splitNote : splitNote.trim();
        }
        
        const orderData = {
          customer_name: customerName,
          customer_email: customerEmail,
          termsAgreed,
          orderNotes: collectedOrderNotes,
          username,
          line_items: localCartItems.map(item => {
            const offeredPrice = item.isCash ? item.cashPrice : item.creditPrice;
            const lowPrice = item.originalBasePrice || item.basePrice;
            
            console.log('Item data:', {
              name: item.name,
              originalBasePrice: item.originalBasePrice,
              basePrice: item.basePrice,
              cashPrice: item.cashPrice,
              creditPrice: item.creditPrice,
              offeredPrice,
              lowPrice,
              condition: item.condition,
              printing: item.printing,
              skuId: item.skuId,
              productId: item.productId,
              language: item.language || 'EN'
            });

            if (!lowPrice) {
              console.warn(`Warning: lowPrice is not set for item ${item.name}`);
            }

            // Ensure skuId is a number or null
            let skuId = null;
            if (item.skuId !== undefined && item.skuId !== null) {
              skuId = Number(item.skuId);
              if (isNaN(skuId)) {
                console.warn(`Invalid skuId for item ${item.name}: ${item.skuId}`);
                skuId = null;
              }
            }

            // Collect any order notes from items
            if (item.orderNote && !collectedOrderNotes.includes(item.orderNote)) {
              if (collectedOrderNotes) {
                collectedOrderNotes += "\n" + item.orderNote;
              } else {
                collectedOrderNotes = item.orderNote;
              }
            }
            
            return {
              productId: item.productId, // Use item.productId instead of item._id
              skuId: skuId,
              name: item.name,
              printType: item.printing,
              condition: item.condition,
              language: item.language || 'EN',
              quantity: item.quantity,
              offeredCashPrice: item.cashPrice,
              offeredCreditPrice: item.creditPrice,
              type: item.isCash ? 'Cash' : 'Credit',
              offeredPrice: offeredPrice,
              lowPrice: lowPrice,
            };
          }),
        };

    console.log('Full order data:', orderData);
    console.log('Submitting order to:', `${BACKEND_URL}/orders`);

    try {
      const response = await fetch(`${BACKEND_URL}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      console.log('Order submitted successfully:', data);
      setOrderCompleted(true);
      setShowCompletionPopup(true);

      // Don't send ORDER_COMPLETED here - it will be sent when user closes the popup
    } catch (err) {
      console.error('Error submitting order:', err);
      setError(`An error occurred during checkout: ${err.message}. Please try again or contact support if the issue persists.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // handleUpdateQuantity removed - cart now uses separate line items

  const handleRemoveFromCart = (item) => {
    const updatedItems = localCartItems.filter(cartItem => cartItem.cartItemId !== item.cartItemId);
    setLocalCartItems(updatedItems);
    removeFromCart(item);
    
    window.parent.postMessage({ type: 'CART_UPDATED', cartItems: updatedItems }, '*');
  };

  const handleTogglePaymentType = (item) => {
    const updatedItems = localCartItems.map(cartItem =>
      cartItem.cartItemId === item.cartItemId
        ? { ...cartItem, isCash: !cartItem.isCash }
        : cartItem
    );
    setLocalCartItems(updatedItems);
    togglePaymentType(item);
    
    window.parent.postMessage({ type: 'CART_UPDATED', cartItems: updatedItems }, '*');
  };

  const openTermsAndConditions = (e) => {
    e.preventDefault();
    setShowTermsPopup(true);
  };

  // Note: quantity is always 1 now (separate line items)
  const calculateTotal = () => {
    if (shouldUsePaymentSplit) return paymentSplitData.cashAmount + paymentSplitData.creditAmount;
    return localCartItems.reduce((total, item) => {
      const price = item.isCash ? item.cashPrice : item.creditPrice;
      return total + price;
    }, 0);
  };

  const handleCompletionPopupClose = () => {
    setShowCompletionPopup(false);
    setCustomerName('');
    setCustomerEmail('');
    setOrderNotes('');
    setTermsAgreed(false);
    setLocalCartItems([]);
    setOrderCompleted(false);

    // Send ORDER_COMPLETED message to reset the app and clear cart
    window.parent.postMessage({ type: 'ORDER_COMPLETED' }, '*');
  };

  const renderCheckoutContent = () => {
    if (orderCompleted) {
      return null;
    }
    return (
      <div className="checkout-page">
        <div className="checkout-header">
          <button onClick={goToMainPage} className="back-button">
            <span className="back-arrow">&#8592;</span> Add More Cards
          </button>
          <h1>Secure Checkout</h1>
        </div>
        {error && <div className="error" role="alert">{error}</div>}
        <div className="checkout-content">
          <div className="order-summary">
            <h2>Order Summary</h2>
            <Cart
              cartItems={localCartItems}
              removeFromCart={handleRemoveFromCart}
              togglePaymentType={handleTogglePaymentType}
              hideCheckoutButton={true}
              paymentSplitData={paymentSplitData}
              clearCart={() => {
                setLocalCartItems([]);
                window.parent.postMessage({ type: 'CLEAR_CART' }, '*');
              }}
              userSettings={userSettings}
              onSaveForLater={onSaveForLater}
              onLoadBuylist={onLoadBuylist}
              setCartItems={(items) => {
                setLocalCartItems(items);
                if (setParentCartItems) setParentCartItems(items);
              }}
            />
            <div className="total">
              <strong>Total: {userSettings && userSettings.currency ?
                (currencySymbols[userSettings.currency] || '$') : '$'}
                {calculateTotal().toFixed(2)}</strong>
            </div>

            {/* Payment Split Summary */}
            {shouldUsePaymentSplit && (
              <div className="payment-split-summary">
                <h3>Payment Split</h3>
                <div className="split-details">
                  <div className="split-row">
                    <span>Cash Amount:</span>
                    <span>{currencySymbols[userSettings?.currency || 'USD'] || '$'}{paymentSplitData.cashAmount.toFixed(2)} ({paymentSplitData.cashPercentage.toFixed(1)}%)</span>
                  </div>
                  <div className="split-row">
                    <span>Credit Amount:</span>
                    <span>{currencySymbols[userSettings?.currency || 'USD'] || '$'}{paymentSplitData.creditAmount.toFixed(2)} ({paymentSplitData.creditPercentage.toFixed(1)}%)</span>
                  </div>
                  <div className="split-row split-total">
                    <span>Split Total:</span>
                    <span>{currencySymbols[userSettings?.currency || 'USD'] || '$'}{(paymentSplitData.cashAmount + paymentSplitData.creditAmount).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="checkout-form">
            <h2>Customer Information</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="customerName">Name</label>
                <input
                  id="customerName"
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  placeholder="Enter your full name"
                  aria-required="true"
                />
              </div>
              <div className="form-group">
                <label htmlFor="customerEmail">Email</label>
                <input
                  id="customerEmail"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  required
                  placeholder="Enter your email address"
                  aria-required="true"
                />
              </div>
              <div className="form-group">
                <label htmlFor="orderNotes">Order Notes (optional)</label>
                <textarea
                  id="orderNotes"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Any special instructions or comments?"
                />
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={termsAgreed}
                    onChange={(e) => setTermsAgreed(e.target.checked)}
                    required
                    aria-required="true"
                  />
                  <span>I agree to the <a href="#" onClick={openTermsAndConditions}>terms and conditions</a></span>
                </label>
              </div>
              <div className="button-group">
                <button type="submit" className="submit-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Processing...' : 'Submit Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
        <div className="checkout-footer">
          <p>Secure checkout powered by <a href="https://tcgsync.com" target="_blank" rel="noopener noreferrer">TCGSync</a></p>
          <div className="security-badges">
            <span className="security-badge">SSL Secured</span>
            <span className="security-badge">256-bit Encryption</span>
          </div>
        </div>
        {showTermsPopup && (
          <TermsAndConditionsPopup
            termsAndConditions={userSettings.tncS}
            onClose={() => setShowTermsPopup(false)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {showCompletionPopup && (
        <OrderCompletionPopup 
          onClose={handleCompletionPopupClose} 
          isProcessing={isSubmitting}
          orderCompleted={orderCompleted}
          onResetApp={() => window.parent.postMessage({ type: 'RESET_APP' }, '*')}
        />
      )}
      {renderCheckoutContent()}
    </>
  );
};

export default CheckoutPage;
